import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount, useChainId, useSwitchChain, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import BottomNavigation from './BottomNavigation';
import Leaderboard from './Leaderboard';
import AcceptCult from './AcceptCult';
import "./Home.css";

const ENV = {
  API_URL: process.env.REACT_APP_API_URL || 'https://mon-cultists.xyz/api',
  CONTRACT_ADDRESS: process.env.REACT_APP_CONTRACT_ADDRESS || '0x50D06243A56F04dF275F597BA4A29b629F6CBA47',
  NETWORK_ID: Number(process.env.REACT_APP_NETWORK_ID) || 10143,
};

const CONTRACT_ABI = [
  {
    "inputs": [{ "internalType": "string", "name": "_affirmation", "type": "string" }],
    "name": "checkin",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function"
  }
];

const monadTestnet = {
  id: ENV.NETWORK_ID,
  name: 'Monad Testnet',
  network: 'monad-testnet',
  nativeCurrency: { decimals: 18, name: 'MON', symbol: 'MON' },
  rpcUrls: { 
    public: { http: [process.env.REACT_APP_RPC_URL || 'https://testnet-rpc.monad.xyz/'] }, 
    default: { http: [process.env.REACT_APP_RPC_URL || 'https://testnet-rpc.monad.xyz/'] } 
  },
  blockExplorers: { 
    default: { 
      name: 'Monad Explorer', 
      url: process.env.REACT_APP_EXPLORER_URL || 'https://testnet.monadexplorer.com/' 
    } 
  },
  testnet: true,
};

function HomeContent() {
  const location = useLocation();
  const [affirmation, setAffirmation] = useState('');
  const [canCheckIn, setCanCheckIn] = useState(false);
  const [checkinDisabled, setCheckinDisabled] = useState(false);
  const [totalCheckins, setTotalCheckins] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [lastCheckinTimestamp, setLastCheckinTimestamp] = useState(null);

  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const { writeContract, data: txHash, error: submitError } = useWriteContract();
  const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({ hash: txHash });

  const safeFetch = useCallback(async (url, options = {}) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Fetch error:', error);
      toast.error('Network error. Please try again.');
      return null;
    }
  }, []);

  const calculateTimeToNextCheckin = useCallback(() => {
    if (!lastCheckinTimestamp) return null;
  
    const lastCheckinTime = new Date(lastCheckinTimestamp).getTime();
    if (isNaN(lastCheckinTime)) return null;  // Handle invalid timestamps
  
    const now = Date.now();
    const nextCheckinTime = lastCheckinTime + 24 * 60 * 60 * 1000;
    const timeRemaining = nextCheckinTime - now;
  
    if (timeRemaining <= 0) {
      setCanCheckIn(true);
      setCheckinDisabled(false);
      return null;
    }
  
    const hours = Math.floor(timeRemaining / 3600000);
    const minutes = Math.floor((timeRemaining % 3600000) / 60000);
    const seconds = Math.floor((timeRemaining % 60000) / 1000);
  
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, [lastCheckinTimestamp]);  

  useEffect(() => {
    const updateTimeLeft = () => {
      const time = calculateTimeToNextCheckin();
      setTimeLeft(time);
      setCheckinDisabled(time !== null);
    };

    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 1000);
    return () => clearInterval(interval);
  }, [calculateTimeToNextCheckin]);

  useEffect(() => {
    safeFetch(`${ENV.API_URL}/affirmation`)
      .then(data => data && setAffirmation(data.affirmation));
  }, [safeFetch]);

  useEffect(() => {
    if (!address) return;

    safeFetch(`${ENV.API_URL}/total-checkins/${address}`)
      .then(data => data && setTotalCheckins(data.total_checkins));

    safeFetch(`${ENV.API_URL}/last-checkin-timestamp/${address}`)
      .then(data => {
        if (data && data.lastCheckinTimestamp) {
          setLastCheckinTimestamp(data.lastCheckinTimestamp);
        }
      });
  }, [address, safeFetch]);

  useEffect(() => {
    if (isConfirmed && txHash && address) {
      safeFetch(`${ENV.API_URL}/record-checkin`, {
        method: 'POST',
        body: JSON.stringify({ wallet: address, transactionHash: txHash }),
      }).then(data => {
        if (data?.totalCheckins) {
          setTotalCheckins(data.totalCheckins);
          setLastCheckinTimestamp(data.lastCheckinTimestamp);
          toast.success(`Affirmation successful! Total affirmations: ${data.totalCheckins}`);
          setCheckinDisabled(true);
          setCanCheckIn(false);
        }
      });
    }
  }, [isConfirmed, txHash, address, safeFetch]);

  const checkIn = useCallback(() => {
    if (!address) return toast.warn('Please connect your wallet');
    if (chainId !== monadTestnet.id) return toast.warn('Please switch to Monad Testnet');
    
    writeContract({ 
      address: ENV.CONTRACT_ADDRESS, 
      abi: CONTRACT_ABI, 
      functionName: 'checkin', 
      args: [affirmation] 
    });
  }, [address, chainId, affirmation, writeContract]);

  const handleSwitchNetwork = useCallback(() => {
    if (switchChain) switchChain({ chainId: monadTestnet.id });
  }, [switchChain]);

  const renderSubmitError = useMemo(() => {
    if (!submitError) return null;
    
    const errorMessage = submitError.message.includes("User rejected the request") 
      ? "YOU HAVE REJECTED THE OFFERINGS OF THE CULT… AND THEY ARE WATCHING." 
      : submitError.message;
    
    return <p className="error" style={{ color: "red" }}>{errorMessage}</p>;
  }, [submitError]);

  return (
    <div>
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="center" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '3%' }}>
        <ConnectButton />
      </div>

      {location.pathname === "/" && (
        <>
          <h1>MON Cultists</h1>
          <div className="image-container">
            <img src="/images/cultist-bg.png" className="cultist-bg" alt="cultist" width="320" />
            <img src="/images/cultist.png" className="cultist" alt="cultist" width="180" />
          </div>
          <h2 className="affirmation-title">Today's affirmation:</h2>
          <p className="affirmation-desc">"{affirmation}"</p>

          <p className="affirmation-desc">Your affirmations sent to blockchain: {totalCheckins}</p>

          {isConnected && chainId !== monadTestnet.id && (
            <button onClick={handleSwitchNetwork} className="cult-button">Switch to Monad Testnet</button>
          )}

          {isConnected && chainId === monadTestnet.id && (
            <>
              {canCheckIn ? (
                <button 
                  onClick={checkIn} 
                  className="cult-button" 
                  disabled={isConfirming || !canCheckIn}
                >
                  {isConfirming ? 'Confirming...' : 'Send affirmation'}
                </button>
              ) : (
                <button 
                  className="cult-button" 
                  disabled={true}
                >
                  Next affirmation: {timeLeft}
                </button>
              )}
            </>
          )}

          {renderSubmitError}
        </>
      )}

      {location.pathname === "/leaderboard" && <Leaderboard userWallet={address} />}
      {location.pathname === "/acceptCult" && <AcceptCult userWallet={address} />}
    </div>
  );
}

function Home() {
  return (
    <Router>
      <div className="app-container">
        <Routes>
          <Route path="/*" element={<HomeContent />} />
        </Routes>
        <BottomNavigation />
      </div>
    </Router>
  );
}

export default Home;